import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, MapPin, Mail, Phone } from 'lucide-react';

export const TopBar = () => {
  return (
    <div data-testid="topbar" className="bg-dark text-dark-foreground py-2">
      <div className="container-custom">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-primary" />
              <span className="font-medium">7 Rue Jean-Jacques Rousseau, Saint-Chamond</span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              <Mail className="h-3 w-3 text-primary" />
              <span className="font-medium">crossfit.saintchamond@gmail.com</span>
            </div>
            <a href="tel:+33684284087" className="flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="topbar-phone">
              <Phone className="h-3 w-3 text-primary" />
              <span className="font-medium">06 84 28 40 87</span>
            </a>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs">
            <a href="https://www.instagram.com/crossfit_saintchamond" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors font-medium">Instagram</a>
            <span className="text-white/30">|</span>
            <a href="https://www.facebook.com/CrossFitSaintChamond" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors font-medium">Facebook</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Accueil', path: '/' },
    { label: 'Programmes', path: '/programmes' },
    { label: 'Planning', path: '/planning' },
    { label: 'Réserver', path: '/reservation' },
    { label: 'Tarifs', path: '/tarifs' },
    { label: 'Blog', path: '/blog' },
    { label: 'À Propos', path: '/about' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <nav data-testid="main-nav" className={`bg-white sticky top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'shadow-lg' : 'shadow-sm'
    }`}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-24 lg:h-28">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_crossfit-showcase/artifacts/f7rp3nhg_image.png"
              alt="CrossFit Saint-Chamond"
              className="h-20 sm:h-24 lg:h-[6.5rem] w-auto"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace(/\//g, '') || 'home'}`}
                className={`px-4 py-2 font-medium text-sm transition-colors rounded-md ${
                  location.pathname === link.path
                    ? 'text-primary bg-accent'
                    : 'text-foreground hover:text-primary hover:bg-accent'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link to="/contact" data-testid="nav-cta-button">
              <Button className="bg-primary hover:bg-primary-dark text-white font-heading text-sm tracking-wide px-6 py-5 rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/25">
                ESSAI GRATUIT
              </Button>
            </Link>
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-border animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col space-y-1 pt-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 font-medium text-sm rounded-lg transition-colors ${
                    location.pathname === link.path
                      ? 'bg-accent text-primary'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
